import {
  Add,
  AssignmentInd,
  CallMerge,
  Cancel,
  CheckCircle,
  Group,
  HourglassBottom,
  PersonOff,
  Search,
  Star,
  UploadFile,
} from '@mui/icons-material';
import {
  Alert,
  alpha,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  DataGridPro,
  GridApiPro,
  GridColDef,
  GridRowSelectionModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  hasContactPortalAccount,
  MARKETING_EMAIL_STATUS_LABEL,
  resolveContactInventoryStage,
  resolveContactMarketingEmailStatus,
  resolveContactMarketingEmailSuppressed,
  resolveContactSalesStage,
  type Contact,
  type ContactStage,
  type MarketingEmailStatus,
} from '@otw/models/leads/contacts';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { salesReps } from '@/constants';
import { compareContactPhoneForSort } from '@/core/contacts/contactIdentity';
import { parseContactsCsv, type ParsedContactsCsvRow } from '@/core/contacts/parseContactsCsv';
import { useAuth } from 'contexts/AuthContext';
import { usePermissions } from 'hooks/auth/usePermissions';
import { useContacts } from 'hooks/contacts/useContacts';
import { useContactCompanies } from 'hooks/contacts/useContactCompanies';
import { getUserDisplayName, useInventoryAssignableUsers, useSalesAssignableUsers, useUsers } from 'hooks/useUsers';
import { useSnackbar } from '@/hooks/common/useSnackbar';
import { formatLastActivityDateTime } from '../../util/activityDisplay';
import { openContactDetailInNewTab } from '../../util/contactRoutes';
import { formatNumber } from '../../util/customerUtils';
import { formatPhoneNumber } from 'utils/formatters';
import { useIsMobile } from 'hooks/screen/useIsMobile';
import {
  CHIP_FONT,
  CHIP_HEIGHT_DENSE,
  CHIP_MAX_WIDTH,
  TOUCH_MIN,
} from 'core/theme.tokens';
import { buildContactCompanyNamesIndex } from '../../util/contactCompanyMembership';
import { buildOwnerDisplayNameIndex } from '../../util/resolveOwnerDisplayName';
import { ContactFormDialog, ImportContactsCsvDialog, MergeContactsDialog } from '../dialogs';
import ContactPreviewPane from '../ContactPreviewPane';
import ListBackToTopBar from '../ListBackToTopBar';
import { ContactListCard } from '../cards';
import { CrmPageActions } from '../CrmPageChrome';

interface ContactsTabProps {
  isActive: boolean;
}

type StageFilter = 'all' | ContactStage;
type OwnerFilter = 'all' | 'unassigned' | string;

interface ContactRow {
  id: string;
  name: string;
  email: string;
  secondaryEmail: string;
  phone: string;
  ownerId: Contact['ownerId'];
  ownerName: string;
  inventoryOwnerId: Contact['inventoryOwnerId'];
  inventoryOwnerName: string;
  companyNames: string[];
  companySearchText: string;
  lastActivityAt: Date | null;
  stage: ContactStage;
  inventoryStage: ContactStage;
  marketingEmailStatus: MarketingEmailStatus;
  marketingEmailSuppressed: boolean;
  hasPortalAccount: boolean;
}

const STAGE_LABEL: Record<ContactStage, string> = {
  prospect: 'Prospect',
  opportunity: 'Opportunity',
  customer: 'Customer',
  unqualified: 'Unqualified',
};

const SEARCH_DEBOUNCE_MS = 300;
const CONTACTS_LIST_HEIGHT = '75vh';
const MOBILE_CARD_ESTIMATE_PX = 152;

const mobileListCardChromeSx = {
  minWidth: 0,
  maxWidth: '100%',
  overflow: 'hidden',
  '& .MuiChip-root': {
    maxWidth: CHIP_MAX_WIDTH,
    height: CHIP_HEIGHT_DENSE,
    fontSize: CHIP_FONT,
    fontWeight: 600,
    '& .MuiChip-label': {
      px: 0.5,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  '& .MuiButton-root': { minHeight: TOUCH_MIN },
  '& .MuiIconButton-root': { minWidth: TOUCH_MIN, minHeight: TOUCH_MIN },
} as const;

const stageAccentColor = (stage: ContactStage, palette: Theme['palette']): string => {
  if (stage === 'customer') return palette.success.main;
  if (stage === 'opportunity') return palette.info.main;
  if (stage === 'unqualified') return palette.text.secondary;
  return palette.warning.main;
};

const useDebouncedValue = (value: string, delayMs: number): string => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
};

const ContactsTab: React.FC<ContactsTabProps> = ({ isActive }) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const { userInfo } = useAuth();
  const { leadManagementScope } = usePermissions();
  const { contacts, isLoading, mergeContacts, isMerging, createContact, updateContact } =
    useContacts();
  const {
    companies: contactCompanies,
    createCompany,
    addCompanyMember,
  } = useContactCompanies();
  const { users: assignableOwners } = useSalesAssignableUsers();
  const { users: inventoryAssignableOwners } = useInventoryAssignableUsers();
  const { users: allUsers } = useUsers();
  const { showError } = useSnackbar();

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebouncedValue(searchQuery, SEARCH_DEBOUNCE_MS);
  const [stageFilter, setStageFilter] = useState<StageFilter>('all');
  const [ownerFilter, setOwnerFilter] = useState<OwnerFilter>('all');
  const [inventoryOwnerFilter, setInventoryOwnerFilter] = useState<OwnerFilter>('all');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [mergePickMode, setMergePickMode] = useState(false);
  const [mergeSelection, setMergeSelection] = useState<string[]>([]);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importRows, setImportRows] = useState<ParsedContactsCsvRow[]>([]);
  const [importTimestamp, setImportTimestamp] = useState<Date | null>(null);
  const [isParsingCsv, setIsParsingCsv] = useState(false);
  const csvFileInputRef = useRef<HTMLInputElement>(null);
  const mobileListRef = useRef<HTMLDivElement>(null);
  const desktopGridApiRef = useGridApiRef();

  const scrollDesktopListToTop = useCallback(() => {
    const root = desktopGridApiRef.current?.rootElementRef?.current;
    const scroller = root?.querySelector(
      '.MuiDataGrid-virtualScroller',
    ) as HTMLElement | null;
    scroller?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [desktopGridApiRef]);

  const scrollMobileListToTop = useCallback(() => {
    mobileListRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const exitMergePickMode = useCallback(() => {
    setMergePickMode(false);
    setMergeSelection([]);
  }, []);

  const handleMergeButtonClick = useCallback(() => {
    if (!mergePickMode) {
      setMergePickMode(true);
      setMergeSelection([]);
      return;
    }
    if (mergeSelection.length === 2) {
      setMergeDialogOpen(true);
    }
  }, [mergePickMode, mergeSelection.length]);

  const handleToggleMergeSelect = useCallback((id: string) => {
    if (!mergePickMode) return;
    setMergeSelection((current) => {
      if (current.includes(id)) {
        return current.filter((candidate) => candidate !== id);
      }
      const next = [...current, id];
      return next.length <= 2 ? next : next.slice(-2);
    });
  }, [mergePickMode]);

  const handleImportCsvClick = useCallback(() => {
    csvFileInputRef.current?.click();
  }, []);

  const handleCsvFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;

      setIsParsingCsv(true);
      try {
        const uploadTimestamp = new Date();
        const result = await parseContactsCsv(file, {
          contacts,
          users: allUsers,
          salesOwners: assignableOwners,
          inventoryOwners: inventoryAssignableOwners,
          salesReps,
        });
        if (result.rows.length === 0) {
          showError('No contact rows found in that CSV.');
          return;
        }
        setImportRows(result.rows);
        setImportTimestamp(uploadTimestamp);
        setImportDialogOpen(true);
      } catch (err) {
        showError(err instanceof Error ? err.message : 'Failed to parse CSV.');
      } finally {
        setIsParsingCsv(false);
      }
    },
    [allUsers, assignableOwners, contacts, inventoryAssignableOwners, showError],
  );

  const selectedContact = useMemo(
    () => contacts.find((c) => c.id === selectedContactId) ?? null,
    [contacts, selectedContactId],
  );

  const scoped = useMemo(() => {
    if (leadManagementScope === 'none') return [];
    if (leadManagementScope === 'all') return contacts;
    if (!userInfo?.id) return [];
    const userId = String(userInfo.id);
    return contacts.filter((c) => {
      const salesOwner = c.ownerId;
      const inventoryOwner = c.inventoryOwnerId;
      const salesMatch =
        salesOwner == null || salesOwner === '' || String(salesOwner) === userId;
      const inventoryMatch =
        inventoryOwner == null ||
        inventoryOwner === '' ||
        String(inventoryOwner) === userId;
      return salesMatch || inventoryMatch;
    });
  }, [contacts, leadManagementScope, userInfo?.id]);

  const companyNamesByContactId = useMemo(
    () => buildContactCompanyNamesIndex(contactCompanies),
    [contactCompanies],
  );

  const ownerNameById = useMemo(
    () => buildOwnerDisplayNameIndex(allUsers),
    [allUsers],
  );

  const resolveOwnerName = useCallback(
    (ownerId: Contact['ownerId']): string => {
      if (ownerId === null || ownerId === undefined || ownerId === '') return 'Unassigned';
      return ownerNameById.get(String(ownerId)) ?? 'Unknown';
    },
    [ownerNameById],
  );

  const rows = useMemo<ContactRow[]>(
    () =>
      scoped.map((c) => {
        const companyNames = companyNamesByContactId.get(c.id) ?? [];
        return {
          id: c.id,
          name: c.name || 'Unnamed contact',
          email: c.email ?? '',
          secondaryEmail: c.secondaryEmail ?? '',
          phone: c.phone ?? '',
          ownerId: c.ownerId ?? null,
          ownerName: resolveOwnerName(c.ownerId),
          inventoryOwnerId: c.inventoryOwnerId ?? null,
          inventoryOwnerName: resolveOwnerName(c.inventoryOwnerId ?? null),
          companyNames,
          companySearchText: companyNames.join(' ').toLowerCase(),
          lastActivityAt: c.lastActivityAt ?? null,
          stage: resolveContactSalesStage(c),
          inventoryStage: resolveContactInventoryStage(c),
          marketingEmailStatus: resolveContactMarketingEmailStatus(c),
          marketingEmailSuppressed: resolveContactMarketingEmailSuppressed(c),
          hasPortalAccount: hasContactPortalAccount(c.userId),
        };
      }),
    [scoped, companyNamesByContactId, resolveOwnerName],
  );

  const visibleRows = useMemo(() => {
    const q = debouncedSearchQuery.trim().toLowerCase();
    return rows.filter((row) => {
      if (stageFilter !== 'all' && row.stage !== stageFilter) return false;
      if (ownerFilter !== 'all') {
        const isUnassigned =
          row.ownerId === null || row.ownerId === undefined || row.ownerId === '';
        if (ownerFilter === 'unassigned') {
          if (!isUnassigned) return false;
        } else if (String(row.ownerId) !== String(ownerFilter)) {
          return false;
        }
      }
      if (inventoryOwnerFilter !== 'all') {
        const isUnassigned =
          row.inventoryOwnerId === null ||
          row.inventoryOwnerId === undefined ||
          row.inventoryOwnerId === '';
        if (inventoryOwnerFilter === 'unassigned') {
          if (!isUnassigned) return false;
        } else if (String(row.inventoryOwnerId) !== String(inventoryOwnerFilter)) {
          return false;
        }
      }
      if (!q) return true;
      return (
        row.name.toLowerCase().includes(q) ||
        row.email.toLowerCase().includes(q) ||
        row.secondaryEmail.toLowerCase().includes(q) ||
        row.phone.toLowerCase().includes(q) ||
        row.ownerName.toLowerCase().includes(q) ||
        row.inventoryOwnerName.toLowerCase().includes(q) ||
        row.companySearchText.includes(q)
      );
    });
  }, [rows, debouncedSearchQuery, stageFilter, ownerFilter, inventoryOwnerFilter]);

  const sortedVisibleRows = useMemo(
    () =>
      [...visibleRows].sort((a, b) => {
        const aMs = a.lastActivityAt?.getTime() ?? -Infinity;
        const bMs = b.lastActivityAt?.getTime() ?? -Infinity;
        return bMs - aMs;
      }),
    [visibleRows],
  );

  const mobileRowVirtualizer = useVirtualizer({
    count: sortedVisibleRows.length,
    getScrollElement: () => mobileListRef.current,
    estimateSize: () => MOBILE_CARD_ESTIMATE_PX,
    overscan: 6,
  });

  const handleOpenContactPage = useCallback(
    (contactId: string, event: React.MouseEvent) => {
      event.stopPropagation();
      openContactDetailInNewTab(contactId);
    },
    [],
  );

  const totals = useMemo(() => {
    const total = scoped.length;
    const prospects = scoped.filter((c) => resolveContactSalesStage(c) === 'prospect').length;
    const opportunities = scoped.filter(
      (c) => resolveContactSalesStage(c) === 'opportunity',
    ).length;
    const customers = scoped.filter((c) => resolveContactSalesStage(c) === 'customer').length;
    const unqualified = scoped.filter(
      (c) => resolveContactSalesStage(c) === 'unqualified',
    ).length;
    return { total, prospects, opportunities, customers, unqualified };
  }, [scoped]);

  const columns = useMemo<GridColDef<ContactRow>[]>(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        flex: 1.2,
        minWidth: 240,
        renderCell: (params) => (
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ minWidth: 0, width: '100%' }}
          >
            <Avatar
              onClick={(event) => handleOpenContactPage(params.row.id, event)}
              sx={{
                width: 32,
                height: 32,
                bgcolor: alpha(stageAccentColor(params.row.stage, theme.palette), 0.18),
                color: stageAccentColor(params.row.stage, theme.palette),
                fontSize: 14,
                fontWeight: 700,
                flexShrink: 0,
                cursor: 'pointer',
              }}
            >
              {params.row.name?.charAt(0)?.toUpperCase() || '?'}
            </Avatar>
            <Typography
              variant="body2"
              fontWeight={600}
              noWrap
              onClick={(event) => handleOpenContactPage(params.row.id, event)}
              sx={{
                flex: 1,
                minWidth: 0,
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {params.row.name}
            </Typography>
            <Button
              className="contact-preview-action"
              size="small"
              variant="outlined"
              onClick={(event) => {
                event.stopPropagation();
                setSelectedContactId(params.row.id);
              }}
              sx={{
                flexShrink: 0,
                py: 0.5,
                px: 1,
                minWidth: 0,
                fontSize: 12,
                lineHeight: 1.4,
                borderRadius: 2,
              }}
            >
              Preview
            </Button>
          </Stack>
        ),
      },
      {
        field: 'stage',
        headerName: 'Sales stage',
        flex: 0.6,
        minWidth: 130,
        renderCell: (params) => (
          <Chip
            size="small"
            label={STAGE_LABEL[params.row.stage]}
            sx={{
              height: 22,
              fontSize: 12,
              fontWeight: 600,
              bgcolor: alpha(stageAccentColor(params.row.stage, theme.palette), 0.14),
              color: stageAccentColor(params.row.stage, theme.palette),
              '& .MuiChip-label': { px: 0.5 },
            }}
          />
        ),
      },
      {
        field: 'inventoryStage',
        headerName: 'Inventory stage',
        flex: 0.6,
        minWidth: 140,
        renderCell: (params) => (
          <Chip
            size="small"
            label={STAGE_LABEL[params.row.inventoryStage]}
            sx={{
              height: 22,
              fontSize: 12,
              fontWeight: 600,
              bgcolor: alpha(stageAccentColor(params.row.inventoryStage, theme.palette), 0.14),
              color: stageAccentColor(params.row.inventoryStage, theme.palette),
              '& .MuiChip-label': { px: 0.5 },
            }}
          />
        ),
      },
      {
        field: 'marketingEmailStatus',
        headerName: 'Marketing email',
        flex: 0.7,
        minWidth: 140,
        renderCell: (params) => (
          <Chip
            size="small"
            label={
              params.row.marketingEmailSuppressed
                ? 'Suppressed'
                : MARKETING_EMAIL_STATUS_LABEL[params.row.marketingEmailStatus]
            }
            color={
              params.row.marketingEmailSuppressed ||
              params.row.marketingEmailStatus === 'unsubscribed'
                ? 'default'
                : 'success'
            }
            variant={
              params.row.marketingEmailSuppressed ||
              params.row.marketingEmailStatus === 'unsubscribed'
                ? 'outlined'
                : 'filled'
            }
            sx={{ height: 22, fontSize: 12, fontWeight: 600 }}
          />
        ),
      },
      {
        field: 'hasPortalAccount',
        headerName: 'Account',
        width: 100,
        minWidth: 100,
        align: 'center',
        headerAlign: 'center',
        type: 'boolean',
        renderCell: (params) => (
          <Tooltip
            title={
              params.row.hasPortalAccount
                ? 'Has portal client account'
                : 'No portal client account'
            }
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
              }}
            >
              {params.row.hasPortalAccount ? (
                <CheckCircle sx={{ color: 'success.main', fontSize: 22 }} />
              ) : (
                <Cancel sx={{ color: 'error.main', fontSize: 22 }} />
              )}
            </Box>
          </Tooltip>
        ),
      },
      {
        field: 'companyNames',
        headerName: 'Companies',
        flex: 1.1,
        minWidth: 200,
        valueGetter: (_value, row) => row.companyNames.join(', '),
        sortComparator: (a: string, b: string) => a.localeCompare(b),
        renderCell: (params) => {
          const names = params.row.companyNames;
          if (names.length === 0) {
            return (
              <Typography variant="body2" color="text.secondary">
                —
              </Typography>
            );
          }
          const visible = names.slice(0, 2);
          const overflow = names.length - visible.length;
          return (
            <Tooltip title={names.join(', ')} placement="top" enterDelay={400}>
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{ minWidth: 0, width: '100%', overflow: 'hidden' }}
              >
                {visible.map((name) => (
                  <Chip
                    key={name}
                    size="small"
                    label={name}
                    sx={{
                      height: 22,
                      fontSize: 12,
                      fontWeight: 600,
                      maxWidth: names.length === 1 ? '100%' : '48%',
                      bgcolor: alpha(theme.palette.secondary.main, 0.1),
                      color: theme.palette.secondary.dark,
                      border: `1px solid ${alpha(theme.palette.secondary.main, 0.25)}`,
                      '& .MuiChip-label': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        px: 1,
                      },
                    }}
                  />
                ))}
                {overflow > 0 ? (
                  <Chip
                    size="small"
                    label={`+${overflow}`}
                    sx={{
                      height: 22,
                      fontSize: 12,
                      fontWeight: 700,
                      flexShrink: 0,
                      bgcolor: alpha(theme.palette.text.secondary, 0.1),
                      color: 'text.secondary',
                      '& .MuiChip-label': { px: 0.5 },
                    }}
                  />
                ) : null}
              </Stack>
            </Tooltip>
          );
        },
      },
      {
        field: 'email',
        headerName: 'Email',
        flex: 1,
        minWidth: 200,
        renderCell: (params) =>
          params.value ? (
            <Typography variant="body2" noWrap>
              {params.value}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          ),
      },
      {
        field: 'secondaryEmail',
        headerName: 'Secondary email',
        flex: 1,
        minWidth: 200,
        renderCell: (params) =>
          params.value ? (
            <Typography variant="body2" noWrap>
              {params.value}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          ),
      },
      {
        field: 'phone',
        headerName: 'Phone',
        flex: 0.8,
        minWidth: 150,
        sortComparator: (a: string, b: string) => compareContactPhoneForSort(a, b),
        renderCell: (params) =>
          params.value ? (
            <Typography variant="body2" noWrap>
              {formatPhoneNumber(params.value)}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          ),
      },
      {
        field: 'ownerName',
        headerName: 'Sales owner',
        flex: 0.8,
        minWidth: 160,
        renderCell: (params) => (
          <Typography
            variant="body2"
            color={params.value === 'Unassigned' ? 'text.secondary' : 'text.primary'}
            noWrap
          >
            {params.value}
          </Typography>
        ),
      },
      {
        field: 'inventoryOwnerName',
        headerName: 'Inventory owner',
        flex: 0.8,
        minWidth: 160,
        renderCell: (params) => (
          <Typography
            variant="body2"
            color={params.value === 'Unassigned' ? 'text.secondary' : 'text.primary'}
            noWrap
          >
            {params.value}
          </Typography>
        ),
      },
      {
        field: 'lastActivityAt',
        headerName: 'Last activity',
        flex: 0.9,
        minWidth: 180,
        valueGetter: (_value, row) => row.lastActivityAt ?? null,
        sortComparator: (a: Date | null, b: Date | null) => {
          const aMs = a?.getTime() ?? -Infinity;
          const bMs = b?.getTime() ?? -Infinity;
          return aMs - bMs;
        },
        renderCell: (params) => (
          <Typography
            variant="body2"
            color={params.row.lastActivityAt ? 'text.primary' : 'text.secondary'}
            noWrap
          >
            {formatLastActivityDateTime(params.row.lastActivityAt)}
          </Typography>
        ),
      },
    ],
    [handleOpenContactPage, theme.palette],
  );

  if (!isActive) return null;

  const kpi = (
    icon: React.ReactNode,
    label: string,
    value: React.ReactNode,
    accent: string,
  ) => (
    <Paper
      variant="outlined"
      sx={{
        p: 1.25,
        borderRadius: 2,
        height: '100%',
        background: `linear-gradient(135deg, ${alpha(accent, 0.12)} 0%, ${alpha(accent, 0.02)} 100%)`,
        borderColor: alpha(accent, 0.25),
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Avatar
          variant="rounded"
          sx={{
            bgcolor: alpha(accent, 0.18),
            color: accent,
            width: 32,
            height: 32,
            '& .MuiSvgIcon-root': { fontSize: 18 },
          }}
        >
          {icon}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1, fontSize: '0.65rem' }}>
            {label}
          </Typography>
          <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.15, fontSize: '1rem' }} noWrap>
            {value}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );

  return (
    <Box sx={{ pb: 2, overflowX: 'hidden', maxWidth: '100%' }}>
      <CrmPageActions active={isActive}>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ justifyContent: 'flex-end' }}>
          {mergePickMode ? (
            <Button variant="text" onClick={exitMergePickMode}>
              Cancel merge
            </Button>
          ) : null}
          <Button
            variant="outlined"
            startIcon={<CallMerge />}
            disabled={mergePickMode && mergeSelection.length !== 2}
            onClick={handleMergeButtonClick}
          >
            {!mergePickMode
              ? 'Merge contacts'
              : mergeSelection.length === 2
                ? 'Merge (2 selected)'
                : `Select 2 (${mergeSelection.length}/2)`}
          </Button>
          <Button
            variant="outlined"
            startIcon={isParsingCsv ? <CircularProgress size={16} color="inherit" /> : <UploadFile />}
            disabled={isParsingCsv || mergePickMode}
            onClick={handleImportCsvClick}
          >
            Import CSV
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setEditingContact(null);
              setIsFormOpen(true);
            }}
          >
            Add contact
          </Button>
          <input
            ref={csvFileInputRef}
            type="file"
            accept=".csv,text/csv"
            hidden
            onChange={handleCsvFileChange}
          />
        </Stack>
      </CrmPageActions>

      {/* KPIs */}
      <Grid container spacing={1} sx={{ mb: 1.5 }}>
        <Grid item xs={6} sm={4} md>
          {kpi(<Group />, 'Total contacts', formatNumber(totals.total), theme.palette.primary.main)}
        </Grid>
        <Grid item xs={6} sm={4} md>
          {kpi(
            <AssignmentInd />,
            'Prospects',
            formatNumber(totals.prospects),
            theme.palette.warning.main,
          )}
        </Grid>
        <Grid item xs={6} sm={4} md>
          {kpi(
            <HourglassBottom />,
            'Opportunities',
            formatNumber(totals.opportunities),
            theme.palette.info.main,
          )}
        </Grid>
        <Grid item xs={6} sm={4} md>
          {kpi(<Star />, 'Customers', formatNumber(totals.customers), theme.palette.success.main)}
        </Grid>
        <Grid item xs={6} sm={4} md>
          {kpi(
            <PersonOff />,
            'Unqualified',
            formatNumber(totals.unqualified),
            theme.palette.text.secondary,
          )}
        </Grid>
      </Grid>

      {/* Search + stage filter */}
      <Paper
        variant="outlined"
        sx={{
          p: 1,
          mb: 1.5,
          borderRadius: 2,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1,
          alignItems: { sm: 'center' },
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Search by name, email, secondary email, phone, company, or owner"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1 }}
        />
        <TextField
          select
          size="small"
          label="Sales stage"
          value={stageFilter}
          onChange={(e) => setStageFilter(e.target.value as StageFilter)}
          sx={{ minWidth: { xs: '100%', sm: 200 } }}
        >
          <MenuItem value="all">All stages</MenuItem>
          <MenuItem value="prospect">Prospect</MenuItem>
          <MenuItem value="opportunity">Opportunity</MenuItem>
          <MenuItem value="customer">Customer</MenuItem>
          <MenuItem value="unqualified">Unqualified</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Sales owner"
          value={ownerFilter}
          onChange={(e) => setOwnerFilter(e.target.value as OwnerFilter)}
          sx={{ minWidth: { xs: '100%', sm: 220 } }}
        >
          <MenuItem value="all">All sales owners</MenuItem>
          <MenuItem value="unassigned">Unassigned</MenuItem>
          {assignableOwners.map((user) => (
            <MenuItem key={user.id} value={String(user.id)}>
              {getUserDisplayName(user)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Inventory owner"
          value={inventoryOwnerFilter}
          onChange={(e) => setInventoryOwnerFilter(e.target.value as OwnerFilter)}
          sx={{ minWidth: { xs: '100%', sm: 220 } }}
        >
          <MenuItem value="all">All inventory owners</MenuItem>
          <MenuItem value="unassigned">Unassigned</MenuItem>
          {inventoryAssignableOwners.map((user) => (
            <MenuItem key={user.id} value={String(user.id)}>
              {getUserDisplayName(user)}
            </MenuItem>
          ))}
        </TextField>
      </Paper>

      {mergePickMode ? (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          Select exactly two contacts to merge, then click <strong>Merge</strong>.
        </Alert>
      ) : null}

      {/* Grid + optional preview pane */}
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={240}>
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            alignItems: { xs: 'stretch', md: 'flex-start' },
          }}
        >
        <Paper
          variant="outlined"
          sx={{
            flex: 1,
            minWidth: 0,
            height: CONTACTS_LIST_HEIGHT,
            borderRadius: 3,
            overflow: 'hidden',
            bgcolor: 'background.paper',
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
          }}
        >
          <DataGridPro<ContactRow>
            apiRef={desktopGridApiRef as React.MutableRefObject<GridApiPro>}
            rows={visibleRows}
            columns={columns}
            density="compact"
            checkboxSelection={mergePickMode}
            disableRowSelectionOnClick
            pagination={false}
            hideFooter
            rowSelectionModel={mergePickMode ? mergeSelection : []}
            onRowSelectionModelChange={(model: GridRowSelectionModel) => {
              if (!mergePickMode) return;
              const ids = (Array.isArray(model) ? model : [model]).map(String);
              setMergeSelection(ids.length <= 2 ? ids : ids.slice(-2));
            }}
            initialState={{
              sorting: { sortModel: [{ field: 'lastActivityAt', sort: 'desc' }] },
            }}
            getRowHeight={() => 64}
            sx={{
              border: 'none',
              flex: 1,
              '& .MuiDataGrid-columnHeaders': {
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                fontWeight: 700,
              },
              '& .MuiDataGrid-cell': {
                display: 'flex',
                alignItems: 'center',
              },
              '& .MuiDataGrid-row .contact-preview-action': {
                opacity: 0,
                pointerEvents: 'none',
                transition: 'opacity 0.15s ease-in-out',
              },
              '& .MuiDataGrid-row:hover .contact-preview-action, & .MuiDataGrid-row:focus-within .contact-preview-action':
                {
                  opacity: 1,
                  pointerEvents: 'auto',
                },
              '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                outline: 'none',
              },
              '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
                outline: 'none',
              },
              ...(mergePickMode
                ? {
                    '& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer':
                      {
                        display: 'none',
                      },
                  }
                : {}),
            }}
            slots={{
              noRowsOverlay: () => (
                <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', p: 4 }}>
                  <Typography variant="h6" gutterBottom>
                    {rows.length === 0 ? 'No contacts yet' : 'No matches'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    {rows.length === 0
                      ? 'Contacts will appear here once they are added or migrated from prospects and customers.'
                      : 'Try a different search, stage, or owner filter.'}
                  </Typography>
                </Stack>
              ),
            }}
          />
          <ListBackToTopBar
            onBackToTop={scrollDesktopListToTop}
            disabled={visibleRows.length === 0}
          />
        </Paper>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            width: '100%',
            display: { xs: 'block', md: 'none' },
          }}
        >
          {sortedVisibleRows.length === 0 ? (
            <Paper
              variant="outlined"
              sx={{
                p: 4,
                borderRadius: 3,
                textAlign: 'center',
                bgcolor: alpha(theme.palette.background.default, 0.6),
              }}
            >
              <Typography variant="h6" gutterBottom>
                {rows.length === 0 ? 'No contacts yet' : 'No matches'}
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                {rows.length === 0
                  ? 'Contacts will appear here once they are added or migrated from prospects and customers.'
                  : 'Try a different search, stage, or owner filter.'}
              </Typography>
            </Paper>
          ) : (
            <Paper
              variant="outlined"
              sx={{
                height: CONTACTS_LIST_HEIGHT,
                borderRadius: 3,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box
                ref={mobileListRef}
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflow: 'auto',
                  width: '100%',
                }}
              >
                <Box
                  sx={{
                    height: `${mobileRowVirtualizer.getTotalSize()}px`,
                    width: '100%',
                    position: 'relative',
                  }}
                >
                  {mobileRowVirtualizer.getVirtualItems().map((virtualRow) => {
                    const row = sortedVisibleRows[virtualRow.index];
                    if (!row) return null;
                    return (
                      <Box
                        key={row.id}
                        data-index={virtualRow.index}
                        ref={mobileRowVirtualizer.measureElement}
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          transform: `translateY(${virtualRow.start}px)`,
                          pb: 1.5,
                          ...mobileListCardChromeSx,
                        }}
                      >
                        <ContactListCard
                          row={row}
                          mergePickMode={mergePickMode}
                          isSelected={mergeSelection.includes(row.id)}
                          onToggleSelect={handleToggleMergeSelect}
                          onPreview={setSelectedContactId}
                          onOpenContactPage={handleOpenContactPage}
                        />
                      </Box>
                    );
                  })}
                </Box>
              </Box>
              <ListBackToTopBar onBackToTop={scrollMobileListToTop} />
            </Paper>
          )}
        </Box>
          {selectedContact ? (
            <ContactPreviewPane
              contact={selectedContact}
              onClose={() => setSelectedContactId(null)}
              onEdit={(c) => {
                setEditingContact(c);
                setIsFormOpen(true);
              }}
            />
          ) : null}
        </Box>
      )}

      <ContactFormDialog
        open={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditingContact(null);
        }}
        contact={editingContact}
      />

      <ImportContactsCsvDialog
        open={importDialogOpen}
        onClose={() => {
          setImportDialogOpen(false);
          setImportRows([]);
          setImportTimestamp(null);
        }}
        rows={importRows}
        contacts={contacts}
        users={allUsers}
        companies={contactCompanies}
        salesOwners={assignableOwners}
        inventoryOwners={inventoryAssignableOwners}
        uploadTimestamp={importTimestamp ?? new Date()}
        createContact={createContact}
        updateContact={updateContact}
        createCompany={createCompany}
        addCompanyMember={addCompanyMember}
      />

      <MergeContactsDialog
        open={mergeDialogOpen}
        onClose={() => {
          setMergeDialogOpen(false);
        }}
        contacts={scoped}
        initialPrimaryId={mergeSelection[0] ?? null}
        initialSecondaryId={mergeSelection[1] ?? null}
        isMerging={isMerging}
        onMerge={async (input) => {
          await mergeContacts(input);
          setMergeDialogOpen(false);
          exitMergePickMode();
          setSelectedContactId((current) =>
            current === input.secondaryContactId ? null : current,
          );
        }}
      />
    </Box>
  );
};

export default ContactsTab;
